import { Fragment } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import { Recommendee, RecommendationFileType, RecommendationTarget } from '../types';
import { recommendationFileTypes } from '../recommendationFileTypes';
import { matchRecommendee, renderRecommendeeFullName } from '../utils';
import { useLang } from '../LanguageContextProvider';

interface RecommendationSideBarProps {
    data: Recommendee[];
    selectedTarget: RecommendationTarget | null;
    setSelectedTarget: React.Dispatch<React.SetStateAction<RecommendationTarget | null>>;
}

const RecommendationSideBar: React.FC<RecommendationSideBarProps> = ({ data, selectedTarget, setSelectedTarget }) => {
    const { lang } = useLang();

    const renderRow = (recommendee: Recommendee, fileType: RecommendationFileType) => {
        const document = recommendee.documentsUploaded[fileType];
        const handleClick: React.MouseEventHandler<HTMLButtonElement> = event => {
            event.stopPropagation();
            setSelectedTarget({ recommendee, fileType });
        };

        return (
            <Stack direction="row" display="flex" alignItems="center" sx={{ padding: theme => `0 ${theme.spacing(1)}` }}>
                <Stack direction="row" flex={3} alignItems="center" spacing={1}>
                    {document?.fileUrl ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                    <Typography>{recommendationFileTypes[fileType][lang]}</Typography>
                </Stack>
                <Button
                    onClick={handleClick}
                    sx={{ flex: 2 }}
                    disabled={!!selectedTarget && matchRecommendee(selectedTarget.recommendee, recommendee) && selectedTarget.fileType === fileType}
                >
                    {document?.fileUrl ? (lang === 'en' ? 'View' : '表示') : (lang === 'en' ? 'Upload' : 'アップロード')}
                </Button>
            </Stack>

        );

    };

    return (
        <Box
            width="400px"
            height="100%"
            padding="8px"
            overflow="auto"
            sx={{
                border: theme => `1px solid ${theme.palette.divider}`,
                bgcolor: theme => theme.palette.grey['100']

            }}
        >
            {/* <Toolbar /> */}
            {data.map(recommendee => (
                <Box key={recommendee.applicantNum} data-testid="recommendee-card" padding="16px">
                    <Paper
                        onClick={() => {
                            if (!selectedTarget || !matchRecommendee(selectedTarget.recommendee, recommendee))
                                setSelectedTarget({ recommendee, fileType: Object.keys(recommendee.documentsUploaded)[0] as RecommendationFileType });
                        }}
                        sx={{
                            border: theme => `2px solid ${theme.palette.background.paper}`,
                            ...(selectedTarget ? {
                                ...(matchRecommendee(selectedTarget.recommendee, recommendee) ? {
                                    border: theme => `2px solid ${theme.palette.primary.main}`
                                } : { opacity: 0.7 })
                            } : {}),
                            cursor: 'pointer'
                        }}
                    >
                        <Stack textAlign="center">
                            <Stack direction="row" display="flex" alignItems="center" padding="8px 16px">
                                <Avatar alt={renderRecommendeeFullName(recommendee, lang)} src={recommendee.imageUrl} />
                                <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>{renderRecommendeeFullName(recommendee, lang)}</Typography>
                            </Stack>
                            <Divider />
                            <Stack flex={1} padding="8px">
                                <Stack>
                                    {Object.keys(recommendee.documentsUploaded).map(key => (
                                        <Fragment key={`${recommendee.applicantNum}-${key}`}>
                                            {renderRow(recommendee, key as RecommendationFileType)}
                                        </Fragment>
                                    ))}
                                </Stack>
                            </Stack>
                        </Stack>
                    </Paper>
                </Box>
            ))}
        </Box>
    );
};

export default RecommendationSideBar;